/**
 * use-affiliate.ts — Hooks Tiếp thị liên kết cho người dùng app (đối tác = user đăng nhập).
 * Backend: ecdp_backend_source/src/modules/affiliate/affiliate-routes.ts
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import type { CommissionItem, PartnerView, PublicProgram, TaxPreview, WithdrawalItem } from '@/lib/affiliate'

const KEY = ['affiliate'] as const

export interface AffiliateMe {
  program: PublicProgram
  partner: PartnerView | null
  blockedReason: string | null
}

export interface AffiliateSummary {
  clicks: number
  clicks30d: number
  leads: number
  referrals: number
  payingCustomers: number
  commission: { totalEarned: number; holding: number; available: number; pendingWithdrawal: number; paid: number }
  minWithdrawal: number
  holdDays: number
}

export interface ReferralItem {
  id: string
  customer: string
  source: string
  attributedAt: string
  paying: boolean
  commissionUntil: string | null
  orders: number
  commission: number
}

export interface OrgReferralStatus {
  referred: boolean
  referrer: { code: string; partnerName: string } | null
  locked: boolean
  canEnter: boolean
  deadline: string | null
  bindWindowDays: number
  hasOwnPartner: boolean
}

export function useAffiliateMe() {
  return useQuery<AffiliateMe>({
    queryKey: [...KEY, 'me'],
    queryFn: async () => (await api.get<AffiliateMe>('/affiliate/me')).data,
  })
}

export function useAffiliateSummary(enabled: boolean) {
  return useQuery<AffiliateSummary>({
    queryKey: [...KEY, 'summary'],
    enabled,
    queryFn: async () => (await api.get<AffiliateSummary>('/affiliate/summary')).data,
  })
}

export function useAffiliateReferrals(enabled: boolean) {
  return useQuery<ReferralItem[]>({
    queryKey: [...KEY, 'referrals'],
    enabled,
    queryFn: async () => (await api.get<{ items: ReferralItem[] }>('/affiliate/referrals')).data.items,
  })
}

export function useAffiliateCommissions(enabled: boolean, status: string, page: number) {
  return useQuery<{ items: CommissionItem[]; total: number; page: number; limit: number }>({
    queryKey: [...KEY, 'commissions', status, page],
    enabled,
    queryFn: async () => (await api.get('/affiliate/commissions', { params: { status: status || undefined, page } })).data,
  })
}

export function useAffiliateWithdrawals(enabled: boolean) {
  return useQuery<{ items: WithdrawalItem[]; balance: { available: number; holding: number; pendingWithdrawal: number; paid: number }; minWithdrawal: number }>({
    queryKey: [...KEY, 'withdrawals'],
    enabled,
    queryFn: async () => (await api.get('/affiliate/withdrawals')).data,
  })
}

function useAffiliateMutation<TIn, TOut>(fn: (input: TIn) => Promise<TOut>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: KEY }) })
}

export const useRegisterPartner = () =>
  useAffiliateMutation(async (input: Record<string, unknown>) => (await api.post<{ partner: PartnerView }>('/affiliate/register', input)).data)
export const useUpdateProfile = () =>
  useAffiliateMutation(async (input: Record<string, unknown>) => (await api.put<{ partner: PartnerView }>('/affiliate/profile', input)).data)
export const useUpdatePayoutInfo = () =>
  useAffiliateMutation(async (input: Record<string, unknown>) => (await api.put<{ partner: PartnerView }>('/affiliate/payout-info', input)).data)
export const useChangeCode = () =>
  useAffiliateMutation(async (code: string) => (await api.put<{ partner: PartnerView }>('/affiliate/code', { code })).data)
export const useCreateWithdrawal = () =>
  useAffiliateMutation(async (amount: number) => (await api.post<{ withdrawal: WithdrawalItem }>('/affiliate/withdrawals', { amount })).data)
export const useCancelWithdrawal = () =>
  useAffiliateMutation(async (id: string) => (await api.post(`/affiliate/withdrawals/${id}/cancel`)).data)

export async function previewWithdrawal(amount: number): Promise<TaxPreview> {
  return (await api.post<{ tax: TaxPreview }>('/affiliate/withdrawals/preview', { amount })).data.tax
}

export function useOrgReferral() {
  return useQuery<OrgReferralStatus>({
    queryKey: [...KEY, 'org-referral'],
    queryFn: async () => (await api.get<OrgReferralStatus>('/affiliate/org-referral')).data,
  })
}

export const useBindOrgReferral = () =>
  useAffiliateMutation(async (code: string) => (await api.post<OrgReferralStatus>('/affiliate/org-referral', { code })).data)
